import lodashGet from 'lodash.get';
import { useMemo } from 'react';
import type { DataPoint, YAxisParams } from '@/widgets/chartsView';
import type {
  MetricColumnDataType,
  MetricDataFormatType,
} from '@/entities/columnsConfig';
import { toFormatType } from '@/entities/columnsConfig';
import { selectCurrency } from '@/entities/session';
import { useNumberFormatter } from '@/shared/hooks';
import { useAppSelector } from '@/shared/model';
import type { Meta } from '../../model/types';
import { OTHERS_KEY, TOTAL_KEY } from './constants';

interface Params<T extends string> {
  config?: Meta['config'];
  metric: string;
  data: DataPoint<T>[];
  splitOptions?: string[];
  excludedSplitOptions: string[];
}

interface Line {
  name: string;
  value: string;
}

interface ChartParams<T extends string> {
  noData: boolean;
  lines: Line[];
  yAxis?: ReturnType<typeof getYAxisParams>;
  data: DataPoint<T>[];
}

export function useChartParams<T extends string>({
  config,
  metric,
  data,
  splitOptions,
  excludedSplitOptions,
}: Params<T>): ChartParams<T> {
  const userCurrency = useAppSelector(selectCurrency);
  const formattersMap = useNumberFormatter(userCurrency);
  const dataWithOthersAndTotal = useMemo(() => {
    if (!config || !config[metric]) {
      return [];
    }

    return data.map((row) => {
      const others = excludedSplitOptions.reduce((acc, option) => {
        const value = lodashGet(
          row,
          `data['${option}'].${metric}`,
          0,
        ) as number;

        return acc + prepareValue(value);
      }, 0);
      const total = (splitOptions || []).reduce((acc, option) => {
        const value = lodashGet(
          row,
          `data['${option}'].${metric}`,
          0,
        ) as number;

        return acc + prepareValue(value);
      }, 0);

      return {
        ...row,
        data: {
          ...Object.fromEntries(
            (splitOptions || []).map((option) => {
              return [
                option,
                {
                  [metric]: prepareValue(row.data[option]?.[metric] ?? 0),
                },
              ];
            }),
          ),
          ...(others > 0 && {
            [OTHERS_KEY]: {
              [metric]: others,
            },
          }),
          [TOTAL_KEY]: { [metric]: total },
        },
      };
    });
  }, [data, excludedSplitOptions, splitOptions, metric, config]);
  const lines = useMemo<Line[]>(() => {
    const preparedSplitOptions = (splitOptions || []).map((option) => {
      return {
        name: option,
        value: option,
      };
    });

    return [
      {
        name: 'Others',
        value: OTHERS_KEY,
      },
      ...preparedSplitOptions,
    ];
  }, [splitOptions]);

  if (!config || !config[metric]) {
    return {
      noData: true,
      lines: [],
      yAxis: undefined,
      data: [],
    };
  }

  const yAxis = getYAxisParams({
    config,
    metric,
    data: dataWithOthersAndTotal,
    lines,
    formattersMap,
  });

  return {
    noData: yAxis.noData,
    lines,
    yAxis,
    data: dataWithOthersAndTotal,
  };
}

const getYAxisParams = ({
  config,
  metric,
  data,
  lines,
  formattersMap,
}: {
  config?: Meta['config'];
  metric: string;
  data: unknown[];
  lines: Line[];
  formattersMap: ReturnType<typeof useNumberFormatter>;
}): Omit<YAxisParams, 'maxValue' | 'minValue'> => {
  if (!config || !metric) {
    return {
      unit: '',
      maxLabelLength: 1,
      dataPropLabel: '',
      tickFormatter: () => '',
      getDataProp: () => '',
      noData: false,
    };
  }

  const metricConfig = config[metric];
  const tickFormatter =
    formattersMap[metricConfig.type as MetricColumnDataType][
      toFormatType(metricConfig.dataType as MetricDataFormatType)
    ];
  const getDataProp = (lineProp: string) => `data['${lineProp}'].${metric}`;
  const noData = data.every((row) => {
    return lines.every((line) => {
      return lodashGet(row, getDataProp(line.value)) === undefined;
    });
  });

  return {
    unit: metric,
    maxLabelLength: data.reduce<number>((acc, row) => {
      return Math.max(
        acc,
        ...lines.map((line) => {
          return tickFormatter(lodashGet(row, getDataProp(line.value), 0))
            .length;
        }),
      );
    }, 0),
    noData,
    dataPropLabel: metricConfig.name,
    tickFormatter,
    getDataProp,
  };
};

// decided to ignore negative values
const prepareValue = (value: number) => {
  return Math.max(value, 0);
};
